
import React, { useCallback, useEffect, useState } from "react";
import { Bell, RefreshCw, Plus, CalendarDays } from "lucide-react";
import "./AdminJobAlerts.css";

const API_URL = "/api/admin/jobs/recent";

const formatDate = (value) => {
  if (!value) return "Date unavailable";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatSalary = (job) => {
  if (job.salaryMin == null && job.salaryMax == null) return null;

  const format = (amount) =>
    `₹${Number(amount).toLocaleString("en-IN")}`;

  if (job.salaryMin != null && job.salaryMax != null) {
    return `${format(job.salaryMin)} – ${format(job.salaryMax)}`;
  }

  return job.salaryMin != null
    ? `From ${format(job.salaryMin)}`
    : `Up to ${format(job.salaryMax)}`;
};

export default function AdminJobAlerts() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [alertStates, setAlertStates] = useState({});
  const [showCreateInfo, setShowCreateInfo] = useState(false);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(API_URL);
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error("The API response is not a job list.");
      }

      // Newest jobs first, using the real database creation date.
      data.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });

      setJobs(data);

      // These are UI-only alert toggles, not job approval statuses.
      setAlertStates((previous) => {
        const next = { ...previous };

        data.forEach((job) => {
          if (next[job.jobId] === undefined) {
            next[job.jobId] = true;
          }
        });

        return next;
      });
    } catch (err) {
      setError(err.message || "Could not load jobs from the backend.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const filteredJobs = jobs.filter((job) => {
    const text = [
      job.jobTitle,
      job.location,
      job.employeeType,
      job.skillsRequired,
      job.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return text.includes(search.toLowerCase().trim());
  });

  return (
    <main className="admin-job-alerts">
      <div className="ja-topbar">
        <div>
          <h1>Job Alerts</h1>
          <p>Recent jobs posted on CertED.</p>
        </div>

        <button
          className="ja-create-btn"
          onClick={() => setShowCreateInfo((value) => !value)}
          type="button"
        >
          <Plus size={18} />
          Create Alert
        </button>
      </div>

      {showCreateInfo && (
        <div className="ja-info">
          This page currently displays recent job posts. Creating and saving
          custom alert preferences requires an alert database/API.
          <button onClick={() => setShowCreateInfo(false)} type="button">
            Close
          </button>
        </div>
      )}

      <div className="ja-controls">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search jobs by title, location, or skills..."
          aria-label="Search recent jobs"
        />

        <button
          className="ja-refresh-btn"
          onClick={loadJobs}
          type="button"
          disabled={loading}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="ja-error" role="alert">
          {error}
          <button onClick={loadJobs} type="button">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="ja-message">Loading recent jobs...</div>
      ) : filteredJobs.length === 0 ? (
        <div className="ja-message">
          {jobs.length === 0 ? "No jobs have been posted yet." : "No matching jobs found."}
        </div>
      ) : (
        <section className="ja-list">
          {filteredJobs.map((job) => (
            <article className="ja-card" key={job.jobId}>
              <div className="ja-bell">
                <Bell size={21}  color="#648dba" />
              </div>

              <div className="ja-card-content">
                <h2>{job.jobTitle || "Untitled job"}</h2>

                <div className="ja-subtitle">
                  {job.skillsRequired || "Skills not specified"}
                  {" • "}
                  {job.location || "Location not specified"}
                </div>

                <div className="ja-date">
                  <CalendarDays size={15} />
                  Posted: {formatDate(job.createdAt)}
                </div>

                <div className="ja-details">
                  {job.employeeType && <span>{job.employeeType.replaceAll("_", " ")}</span>}
                  {job.experienceRequired && <span>{job.experienceRequired} experience</span>}
                  {formatSalary(job) && <span>{formatSalary(job)}</span>}
                  {job.vacancy != null && <span>{job.vacancy} vacancies</span>}
                  <span>{job.applicationCount ?? 0} applications</span>
                </div>
              </div>

              <div className="ja-card-status">
                <span className={`ja-job-status ja-${String(job.status || "UNKNOWN").toLowerCase()}`}>
                  {String(job.status || "UNKNOWN").replaceAll("_", " ")}
                </span>

                <button
                  type="button"
                  className={`ja-switch ${alertStates[job.jobId] ? "is-on" : ""}`}
                  role="switch"
                  aria-checked={Boolean(alertStates[job.jobId])}
                  aria-label={`Toggle alert for ${job.jobTitle || "job"}`}
                  onClick={() =>
                    setAlertStates((previous) => ({
                      ...previous,
                      [job.jobId]: !previous[job.jobId],
                    }))
                  }
                >
                  <span />
                </button>

                <span className="ja-switch-label">
                  {alertStates[job.jobId] ? "Alert On" : "Alert Off"}
                </span>
              </div>
            </article>
          ))}
        </section>
      )}

      <div className="ja-footer">
        Showing {filteredJobs.length} of {jobs.length} recent jobs
      </div>
    </main>
  );
}
